import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../services/api';
import SmartLink from './SmartLink';
import { useSiteSettings, resolveAsset } from '../services/siteSettings';

// Static menu, used only until / unless the API answers. Same shape as the
// tree built from the API below.
const NAV_LINKS_FALLBACK = [
  {
    label: 'About',
    url: '/about',
    children: [
      { label: 'About Us', url: '/about' },
      { label: 'Awards', url: '/awards' },
    ],
  },
  { label: 'Services', url: '/Services' },
  { label: 'Portfolio', url: '/Portfolio' },
  { label: 'Blog', url: '/blog' },
  { label: 'Contact', url: '/contact' },
  { label: 'Next XR Hub', url: '/Hub' },
];

// Turns the admin's flat Header Menu rows into top-level items, each with its
// sub-menu items (rows whose parent_id points at it) as `children`. Rows
// arrive already ordered by order_index. A sub-item whose parent is missing or
// inactive is hidden along with it.
function buildMenuTree(rows) {
  const active = rows.filter((r) => r && r.is_active);
  const top = active.filter((r) => !r.parent_id);
  return top.map((item) => {
    const children = active.filter((r) => r.parent_id === item.id);
    return children.length ? { ...item, children } : item;
  });
}

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const settings = useSiteSettings();
  const [navLinks, setNavLinks] = useState(NAV_LINKS_FALLBACK);

  useEffect(() => {
    let cancelled = false;
    api
      .get('/api/nav-links')
      .then((res) => {
        if (cancelled) return;
        // The menu is exactly what the admin configures under Header Menu.
        // The six single-service pages are deliberately not listed here — they
        // are reached from the cards on the Services page (see ServiceCards).
        // Sub-menus come from each item's Parent Menu setting.
        const tree = buildMenuTree(Array.isArray(res.data) ? res.data : []);
        if (tree.length) setNavLinks(tree);
      })
      .catch(() => {
        /* keep static fallback if the API is unreachable */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const { pathname } = useLocation();
  const [openSub, setOpenSub] = useState(null);
  const closeMenu = () => setMenuOpen(false);

  const normalise = (p) => (p || '').toLowerCase().replace(/\/+$/, '') || '/';
  const isActive = (url) =>
    !!url && !/^https?:/i.test(url) && normalise(url) === normalise(pathname);

  // Close on navigation; when opening, expand the section holding the
  // current page.
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const parent = navLinks.find((l) => l.children?.some((c) => isActive(c.url)));
    setOpenSub(parent ? parent.id ?? parent.label : null);

    // Lock the page behind the overlay and let Escape close it.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => e.key === 'Escape' && setMenuOpen(false);
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuOpen]);

  const logoSrc = settings?.logo
    ? resolveAsset(settings.logo)
    : '../Images/Website_Logo/NextXR.png';
  const ctaText = settings?.cta_text || 'Book a demo';
  const ctaLink = settings?.cta_link || 'https://calendly.com/nextxrgroup';

  return (
    <>
      <nav id="navbar" className="Navbar-nav">
        <SmartLink to="/" className="logo">
          <img src={logoSrc} alt="Next XR Logo" />
        </SmartLink>

        {/* Desktop nav links */}
        <ul className="nav-links">
          {navLinks.map((link) => {
            const children = link.children;
            if (!children) {
              return (
                <li key={link.id ?? link.label}>
                  <SmartLink to={link.url}>{link.label}</SmartLink>
                </li>
              );
            }
            return (
              <li key={link.id ?? link.label} className="nav-has-sub">
                <SmartLink to={link.url} aria-haspopup="true" onClick={(e) => e?.currentTarget?.blur()}>
                  {link.label}
                  <svg className="nav-caret" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </SmartLink>
                <ul className="nav-sub">
                  {children.map((child) => (
                    <li key={child.id ?? child.url}>
                      {/* Drop focus after navigating, otherwise :focus-within
                          keeps the dropdown open on the new page. */}
                      <SmartLink to={child.url} onClick={(e) => e?.currentTarget?.blur()}>
                        {child.label}
                      </SmartLink>
                    </li>
                  ))}
                </ul>
              </li>
            );
          })}
        </ul>

        {/* Desktop CTA */}
        <button className="btn-outline desktop-cta" onClick={() => window.location.href = ctaLink}>
          <span>{ctaText}</span>
        </button>

        {/* Mobile hamburger */}
        <button
          className="hamburger"
          onClick={() => setMenuOpen(true)}
          aria-label="Open menu"
        >
          &#9776;
        </button>
      </nav>

      {/* Mobile overlay menu */}
      <div
        className={`mobile-menu${menuOpen ? ' open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Site menu"
        aria-hidden={!menuOpen}
      >
        <div className="mobile-menu-header">
          <SmartLink to="/" className="logo" onClick={closeMenu}>
            <img src={logoSrc} alt="Next XR Logo" />
          </SmartLink>
          <button className="close-btn" onClick={closeMenu} aria-label="Close menu">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="18" y1="6" x2="6" y2="18" />
            </svg>
          </button>
        </div>

        <ul className="mobile-nav-links">
          {navLinks.map((link) => {
            const key = link.id ?? link.label;
            const children = link.children;
            const childActive = children?.some((c) => isActive(c.url));
            const subOpen = openSub === key;
            return (
              <li
                key={key}
                className={`mobile-nav-item${isActive(link.url) || childActive ? ' is-active' : ''}`}
              >
                <div className="mobile-nav-row">
                  <SmartLink to={link.url} onClick={closeMenu}>
                    {link.label}
                  </SmartLink>
                  {children && (
                    <button
                      type="button"
                      className={`mobile-nav-toggle${subOpen ? ' open' : ''}`}
                      aria-expanded={subOpen}
                      aria-label={`${subOpen ? 'Hide' : 'Show'} ${link.label} links`}
                      onClick={() => setOpenSub(subOpen ? null : key)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>
                  )}
                </div>
                {children && (
                  <ul className={`mobile-nav-sub${subOpen ? ' open' : ''}`}>
                    {children.map((child) => (
                      <li key={child.id ?? child.url}>
                        <SmartLink
                          to={child.url}
                          onClick={closeMenu}
                          className={isActive(child.url) ? 'is-active' : undefined}
                        >
                          {child.label}
                        </SmartLink>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>

        <button
          className="mobile-cta"
          onClick={() => { window.location.href = ctaLink; closeMenu(); }}
        >
          {ctaText}
        </button>
      </div>

      <style>{`
        /* ─── DESKTOP STYLES (unchanged) ──────────────────────────────── */

        #navbar, .Navbar-nav {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 100;
          padding: 20px 60px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          background: var(--header-bg, rgba(2,2,6,0.6));
          border-bottom: 1px solid rgba(123,47,224,0.1);
          transition: all 0.4s ease;
        }

        #navbar.scrolled, .Navbar-nav.scrolled {
          padding: 14px 60px;
          background: var(--header-bg, rgba(2,2,6,0.9));
        }

        .logo {
          display: flex;
          align-items: center;
        }

        .logo img {
          height: 60px;
          width: auto;
          display: block;
          filter: brightness(1);
        }

        .nav-links {
          display: flex;
          gap: 36px;
          list-style: none;
        }

        .nav-links a {
          text-decoration: none;
          color: var(--header-menu, var(--muted, #6B6880));
          font-size: 14px;
          font-weight: 400;
          letter-spacing: 0.02em;
          transition: color 0.3s;
          position: relative;
        }

        .nav-links a::after {
          content: '';
          position: absolute;
          bottom: -4px;
          left: 0;
          width: 0;
          height: 1px;
          background: var(--violet);
          transition: width 0.3s ease;
        }

        .nav-links a:hover {
          color: var(--header-text, var(--text, #F0EDFF));
        }

        .nav-links a:hover::after {
          width: 100%;
        }

        /* Dropdown sub-menu (desktop). Opens on hover and keyboard focus. */
        .nav-has-sub {
          position: relative;
        }
        .nav-has-sub > a {
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .nav-caret {
          transition: transform 0.25s ease;
        }
        .nav-has-sub:hover .nav-caret,
        .nav-has-sub:focus-within .nav-caret {
          transform: rotate(180deg);
        }
        .nav-sub {
          position: absolute;
          top: 100%;
          left: 50%;
          min-width: 180px;
          margin: 0;
          /* Top padding bridges the gap so the menu stays open while the
             pointer moves down from the parent link. */
          padding: 18px 0 0;
          list-style: none;
          opacity: 0;
          visibility: hidden;
          transform: translate(-50%, 6px);
          transition: opacity 0.2s ease, transform 0.2s ease, visibility 0.2s;
        }
        .nav-sub::before {
          content: '';
          position: absolute;
          inset: 18px 0 0;
          z-index: -1;
          border-radius: 14px;
          border: 1px solid rgba(155,77,255,0.25);
          background: rgba(10,8,20,0.96);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          box-shadow: 0 18px 40px rgba(0,0,0,0.45);
        }
        .nav-has-sub:hover .nav-sub,
        .nav-has-sub:focus-within .nav-sub {
          opacity: 1;
          visibility: visible;
          transform: translate(-50%, 0);
        }
        .nav-sub li:first-child { padding-top: 8px; }
        .nav-sub li:last-child { padding-bottom: 8px; }
        .nav-sub a {
          display: block;
          padding: 10px 20px;
          white-space: nowrap;
        }
        .nav-sub a::after { display: none; }
        .nav-sub a:hover {
          background: rgba(155,77,255,0.12);
        }

        .btn-outline {
          position: relative;
          padding: 15px 30px;
          border-radius: 100px;
          border: 1px solid rgba(255,255,255,0.2);
          font-family: 'DM Sans', sans-serif;
          font-size: 16px;
          font-weight: 500;
          cursor: none;
          overflow: hidden;
          white-space: nowrap;
          background: rgba(255,255,255,0.06);
          backdrop-filter: blur(16px) saturate(1.4);
          -webkit-backdrop-filter: blur(16px) saturate(1.4);
          color: rgba(255,255,255,0.88);
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.14),
            inset 0 -1px 0 rgba(0,0,0,0.1),
            0 4px 16px rgba(0,0,0,0.2);
          transition: all 0.3s ease;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }

        .btn-outline::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 60%);
          pointer-events: none;
        }

        .btn-outline:hover {
          background: rgba(155,77,255,0.15);
          border-color: rgba(155,77,255,0.6);
          color: #fff;
          transform: translateY(-2px);
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.2),
            0 8px 32px rgba(123,47,224,0.25);
        }

        .btn-outline span {
          position: relative;
          z-index: 1;
        }

        /* ─── MOBILE-ONLY ELEMENTS (hidden on desktop) ────────────────── */

        .hamburger {
          display: none;
        }

        .mobile-menu {
          display: none;
        }

        /* ─── MOBILE BREAKPOINT ───────────────────────────────────────── */

        @media (max-width: 768px) {

          .btn-outline { 
           opacity: 100;
          }

          /* Adjust navbar padding on mobile */
          #navbar, .Navbar-nav {
            padding: 16px 20px;
          }

          /* Hide desktop-only elements */
          .nav-links,
          .desktop-cta {
            display: none;
          }

          /* Show hamburger */
          .hamburger {
            display: flex;
            align-items: center;
            justify-content: center;
            background: none;
            border: none;
            color: #fff;
            font-size: 22px;
            cursor: pointer;
            padding: 4px 8px;
            line-height: 1;
          }

          /* Full-screen mobile overlay */
          .mobile-menu {
            display: flex;
            flex-direction: column;
            position: fixed;
            inset: 0;
            z-index: 200;
            background:
              radial-gradient(circle at 100% 0%, rgba(123,47,224,0.22), transparent 55%),
              #05040a;
            padding: 16px 20px max(24px, env(safe-area-inset-bottom));
            overflow-y: auto;
            overscroll-behavior: contain;
            transform: translateX(100%);
            visibility: hidden;
            transition:
              transform 0.35s cubic-bezier(0.4, 0, 0.2, 1),
              visibility 0s linear 0.35s;
          }

          .mobile-menu.open {
            transform: translateX(0);
            visibility: visible;
            transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
          }

          /* Header row inside overlay — mirrors the navbar so the logo and
             close button sit where the logo and hamburger were. */
          .mobile-menu-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 28px;
          }

          .close-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 40px;
            height: 40px;
            background: rgba(255,255,255,0.06);
            border: 1px solid rgba(255,255,255,0.12);
            border-radius: 50%;
            color: #fff;
            cursor: pointer;
            padding: 0;
          }

          /* Nav list — left-aligned full-width rows with dividers */
          .mobile-nav-links {
            list-style: none;
            margin: 0;
            padding: 0;
            display: flex;
            flex-direction: column;
            flex: 1;
          }

          .mobile-nav-item {
            border-bottom: 1px solid rgba(255,255,255,0.08);
          }

          .mobile-nav-row {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
          }

          .mobile-nav-row > a {
            flex: 1;
            display: block;
            padding: 16px 0;
            text-decoration: none;
            color: var(--header-menu, #fff);
            font-family: 'DM Sans', sans-serif;
            font-size: 22px;
            font-weight: 500;
            letter-spacing: -0.01em;
            transition: color 0.2s;
          }

          .mobile-nav-item.is-active > .mobile-nav-row > a,
          .mobile-nav-row > a:hover {
            color: var(--header-text, #9B4DFF);
          }

          .mobile-nav-toggle {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 36px;
            height: 36px;
            flex-shrink: 0;
            background: rgba(255,255,255,0.05);
            border: 1px solid rgba(255,255,255,0.1);
            border-radius: 50%;
            color: #fff;
            cursor: pointer;
            padding: 0;
          }

          .mobile-nav-toggle svg {
            transition: transform 0.25s ease;
          }

          .mobile-nav-toggle.open svg {
            transform: rotate(180deg);
          }

          /* Sub-menu — collapsible, indented under its parent */
          .mobile-nav-sub {
            list-style: none;
            margin: 0;
            padding: 0 0 0 14px;
            border-left: 2px solid rgba(155,77,255,0.45);
            max-height: 0;
            overflow: hidden;
            opacity: 0;
            transition: max-height 0.3s ease, opacity 0.2s ease, margin 0.3s ease;
          }

          .mobile-nav-sub.open {
            max-height: 400px;
            opacity: 1;
            margin: 0 0 14px;
          }

          .mobile-nav-sub a {
            display: block;
            padding: 9px 0;
            text-decoration: none;
            color: rgba(255,255,255,0.6);
            font-family: 'DM Sans', sans-serif;
            font-size: 16px;
            font-weight: 400;
            transition: color 0.2s;
          }

          .mobile-nav-sub a:hover,
          .mobile-nav-sub a.is-active {
            color: var(--header-text, #9B4DFF);
          }

          /* Mobile CTA — full-width purple pill at the bottom */
          .mobile-menu .mobile-cta {
            width: 100%;
            margin-top: 28px;
            background: linear-gradient(135deg, #7B2FE0, #9B4DFF);
            border: none;
            border-radius: 100px;
            padding: 16px 24px;
            font-family: 'DM Sans', sans-serif;
            font-size: 16px;
            font-weight: 600;
            color: #fff;
            cursor: pointer;
            box-shadow: 0 8px 28px rgba(123,47,224,0.4);
            transition: filter 0.2s;
          }

          .mobile-menu .mobile-cta:hover {
            filter: brightness(1.1);
          }
        }
      `}</style>
    </>
  );
};

export default Navbar;
